import { z } from 'zod'
import { REVIEW_BODY_MAX, REVIEW_DECLINE_REASON_MAX, REVIEW_REPLY_MAX, REVIEW_REPORT_MAX } from '../constants/vendor-reviews.js'
import { instantSchema } from '../validators/temporal.validator.js'

/** A review of a company that worked the event: one to five stars, and what it was like (docs/vendor-growth.spec.md, "Reviews"). */
export const writeVendorReviewInputSchema = z
  .object({
    rating: z.number().int().min(1, 'Choose from one to five stars').max(5, 'Choose from one to five stars'),
    body: z
      .string()
      .trim()
      .min(1, 'Say what it was like to work with them')
      .max(REVIEW_BODY_MAX, `Keep it under ${REVIEW_BODY_MAX.toLocaleString('en-US')} characters`),
    /**
     * When the review being changed was sent, as the writer read it; left out
     * for a first review. A review sent again meanwhile, by a colleague at the
     * planning company or in another tab, is refused rather than overwritten.
     */
    sentAt: instantSchema.optional(),
  })
  .strict()

export type WriteVendorReviewInput = z.infer<typeof writeVendorReviewInputSchema>

/**
 * A platform admin's answer to a review waiting for approval. `sentAt` is when
 * the version the admin read was sent: a review changed since is refused, so
 * nothing is published that nobody approved.
 */
export const decideVendorReviewInputSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve'), sentAt: instantSchema }).strict(),
  z
    .object({
      decision: z.literal('decline'),
      sentAt: instantSchema,
      reason: z
        .string()
        .trim()
        .min(1, 'Say why, for the person who wrote it')
        .max(REVIEW_DECLINE_REASON_MAX, `Keep it under ${String(REVIEW_DECLINE_REASON_MAX)} characters`),
    })
    .strict(),
])

export type DecideVendorReviewInput = z.infer<typeof decideVendorReviewInputSchema>

/** The company's one reply to a review of it, for its owners to write (docs/vendor-growth.spec.md, "Reviews"). */
export const writeVendorReviewReplyInputSchema = z
  .object({
    body: z
      .string()
      .trim()
      .min(1, 'Write your reply first')
      .max(REVIEW_REPLY_MAX, `Keep it under ${REVIEW_REPLY_MAX.toLocaleString('en-US')} characters`),
    /** When the reply being changed was sent, as its writer read it; left out for a first reply. */
    sentAt: instantSchema.optional(),
  })
  .strict()

export type WriteVendorReviewReplyInput = z.infer<typeof writeVendorReviewReplyInputSchema>

/** Asking FLOH Venues to look at a review of the company, once, saying why. */
export const reportVendorReviewInputSchema = z
  .object({
    reason: z
      .string()
      .trim()
      .min(1, 'Say what’s wrong with it')
      .max(REVIEW_REPORT_MAX, `Keep it under ${REVIEW_REPORT_MAX.toLocaleString('en-US')} characters`),
  })
  .strict()

export type ReportVendorReviewInput = z.infer<typeof reportVendorReviewInputSchema>

/** A platform admin's answer to a report: keep the review, or take it down for good. */
export const settleVendorReviewReportInputSchema = z.object({ decision: z.enum(['keep', 'remove']) }).strict()

export type SettleVendorReviewReportInput = z.infer<typeof settleVendorReviewReportInputSchema>
